import {
	Box,
	Checkbox,
	Heading,
	Radio,
	RadioGroup,
	Select,
	Stack,
	Text
} from "@chakra-ui/react";
import React from "react";
import {
	AREA_DISCOVER_MODE_ALL,
	AREA_DISCOVER_MODE_PLAYING,
	AREA_DISCOVER_MODE_TRIGGERABLE,
	AREA_FLOW_SEQUENTIAL,
	AREA_FLOW_SPATIAL,
	AreaDiscoverMode,
	AreaFlowMode,
	BLE_SCAN_MODE_DONGLE_ONLY,
	BLE_SCAN_MODE_DONGLE_OR_PHONE,
	BLE_SCAN_MODE_NONE,
	BleScanMode
} from "../../types";
import {resolveBleScanMode} from "./areaSettingsUtils";

type GeneralSettingsTabProps = {
	canEdit: boolean;
	bleScanMode: BleScanMode;
	autoPlay: boolean;
	discoverMode: AreaDiscoverMode;
	useBoundariesZone: boolean;
	flowMode: AreaFlowMode;
	canChooseSequentialFlow: boolean;
	onBleScanModeChange: (value: BleScanMode) => void;
	onAutoPlayChange: (value: boolean) => void;
	onDiscoverModeChange: (value: AreaDiscoverMode) => void;
	onUseBoundariesZoneChange: (value: boolean) => void;
	onFlowModeChange: (value: AreaFlowMode) => void;
};

const GeneralSettingsTab = ({
	canEdit,
	bleScanMode,
	autoPlay,
	discoverMode,
	useBoundariesZone,
	flowMode,
	canChooseSequentialFlow,
	onBleScanModeChange,
	onAutoPlayChange,
	onDiscoverModeChange,
	onUseBoundariesZoneChange,
	onFlowModeChange
}: GeneralSettingsTabProps) => {
	return (
		<Stack spacing={6}>
			{canChooseSequentialFlow ? (
				<Box>
					<Heading size={"sm"} mb={2}>Space flow</Heading>
					<RadioGroup
						value={flowMode}
						onChange={(value) => onFlowModeChange(value as AreaFlowMode)}
						isDisabled={!canEdit}
					>
						<Stack spacing={2}>
							<Radio value={AREA_FLOW_SPATIAL}>
								<Box>
									<Text fontWeight={600}>Spatial (Spot list)</Text>
									<Text fontSize={"sm"} color={"gray.600"}>
										Visitors trigger spots by moving through the space. Rules are authored per-spot.
									</Text>
								</Box>
							</Radio>
							<Radio value={AREA_FLOW_SEQUENTIAL}>
								<Box>
									<Text fontWeight={600}>Sequential (Cue list)</Text>
									<Text fontSize={"sm"} color={"gray.600"}>
										Content plays in a defined order. Rules are authored as a cue sequence.
									</Text>
								</Box>
							</Radio>
						</Stack>
					</RadioGroup>
				</Box>
			) : null}
			<Box>
				<Heading size={"sm"} mb={2}>Spot triggering</Heading>
				<Stack spacing={3} maxW={"520px"}>
					<Checkbox
						isChecked={autoPlay}
						onChange={(event) => onAutoPlayChange(event.target.checked)}
						isDisabled={!canEdit}
					>
						<Box>
							<Text fontWeight={600}>Auto-play spots</Text>
							<Text fontSize={"sm"} color={"gray.600"}>
								When on, spots start playing as soon as the visitor enters their trigger zone. When off, spots wait for a tap.
							</Text>
						</Box>
					</Checkbox>
					<Box>
						<Text fontWeight={600} mb={1}>Spot list shows</Text>
						<Select
							value={discoverMode}
							onChange={(event) => onDiscoverModeChange(event.target.value as AreaDiscoverMode)}
							isDisabled={!canEdit}
							maxW={"320px"}
						>
							<option value={AREA_DISCOVER_MODE_PLAYING}>Only the currently-playing spot</option>
							<option value={AREA_DISCOVER_MODE_TRIGGERABLE}>Spots the visitor can play right now</option>
							<option value={AREA_DISCOVER_MODE_ALL}>All spots in the space</option>
						</Select>
						{discoverMode === AREA_DISCOVER_MODE_ALL ? (
							<Text fontSize={"sm"} color={"orange.600"} mt={2}>
								“All spots” is Android 6.1.0+ only. On iOS, locked spots currently appear as tappable — don&apos;t ship this mode on iOS-visible spaces yet.
							</Text>
						) : null}
					</Box>
				</Stack>
			</Box>
			<Box>
				<Heading size={"sm"} mb={2}>Bluetooth scanning</Heading>
				<RadioGroup
					value={resolveBleScanMode(bleScanMode)}
					onChange={(value) => onBleScanModeChange(resolveBleScanMode(value))}
					isDisabled={!canEdit}
				>
					<Stack spacing={2}>
						<Radio value={BLE_SCAN_MODE_DONGLE_OR_PHONE}>
							<Box>
								<Text fontWeight={600}>Dongle or phone (default)</Text>
								<Text fontSize={"sm"} color={"gray.600"}>
									Use phone scanning unless the USB dongle is verified and connected.
								</Text>
							</Box>
						</Radio>
						<Radio value={BLE_SCAN_MODE_DONGLE_ONLY}>
							<Box>
								<Text fontWeight={600}>Dongle only</Text>
								<Text fontSize={"sm"} color={"gray.600"}>
									Phone scanning stays off; only dongle results are accepted.
								</Text>
							</Box>
						</Radio>
						<Radio value={BLE_SCAN_MODE_NONE}>
							<Box>
								<Text fontWeight={600}>None</Text>
								<Text fontSize={"sm"} color={"gray.600"}>
									Ignore both phone scans and dongle results and clear dongle filters.
								</Text>
							</Box>
						</Radio>
					</Stack>
				</RadioGroup>
			</Box>
			<Box>
				<Heading size={"sm"} mb={2}>Legacy</Heading>
				<Stack spacing={2}>
					<Checkbox
						isChecked={useBoundariesZone}
						onChange={(event) => onUseBoundariesZoneChange(event.target.checked)}
						isDisabled={!canEdit}
					>
						Use Boundaries zone
					</Checkbox>
				</Stack>
			</Box>
		</Stack>
	);
};

export default GeneralSettingsTab;
